import {getCookie} from 'common/utils';
import {SHOPIFY_S_COOKIE, SHOPIFY_Y_COOKIE} from 'common/constants';

import {defineCustomElement} from '../../common/init';
import Bugsnag from '../../common/bugsnag';

import {isShopifyMetadata} from './utils/metadataValidation';

export default class ShopifyPaymentTerms extends HTMLElement {
  static get observedAttributes() {
    return ['variant-id', 'shopify-meta'];
  }

  private _didMount = false;
  private _currentBanner: HTMLElement = document.createElement('div');

  constructor() {
    super();

    this.attachShadow({mode: 'open'});
  }

  connectedCallback() {
    const newBanner = this._getNewBannerNode();

    if (!this.shadowRoot) {
      Bugsnag.notify(new Error('ShopifyPaymentTerms: shadowRoot is null.'));
    }
    this.shadowRoot?.appendChild(newBanner);
    this._currentBanner = newBanner;
    this._didMount = true;
    triggerFinancialTerms();
  }

  attributeChangedCallback() {
    if (this._didMount) {
      const installmentsBanner = this._getNewBannerNode();
      this.shadowRoot?.replaceChild(installmentsBanner, this._currentBanner);
      this._currentBanner = installmentsBanner;
    }
  }

  disconnectedCallback() {
    if (this.shadowRoot?.contains(this._currentBanner)) {
      this.shadowRoot?.removeChild(this._currentBanner);
    }
  }

  private _getNewBannerNode() {
    const shopifyMeta = this.getAttribute('shopify-meta');
    const variantId = this.getAttribute('variant-id');
    const isShopifyMetadataValid = Boolean(
      shopifyMeta && isShopifyMetadata(JSON.parse(shopifyMeta)),
    );
    const installmentsBanner = document.createElement(
      isShopifyMetadataValid
        ? 'shop-pay-installments-banner'
        : 'shop-pay-banner',
    );
    if (variantId) {
      installmentsBanner.setAttribute('variant-id', variantId);
    }
    if (shopifyMeta) {
      installmentsBanner.setAttribute('shopify-meta', shopifyMeta);
    }

    return installmentsBanner;
  }
}

/**
 * This triggers the financial terms query to fetch the experimental packages
 * @returns {void}
 */
async function triggerFinancialTerms() {
  const userToken = getCookie(SHOPIFY_Y_COOKIE);
  const visitToken = getCookie(SHOPIFY_S_COOKIE);
  const headers: Record<string, string> = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Content-Type': 'application/json',
  };

  if (userToken) {
    headers['x-shopify-uniquetoken'] = userToken;
  }

  if (visitToken) {
    headers['x-shopify-visittoken'] = visitToken;
  }

  const query = `
    {
      "query":"query FinancingPlans{spiFinancingPlans{__typename}}",
      "variables":{},
      "operationName":"FinancingPlans"
    }
  `;

  try {
    await fetch(`${window.location.origin}/checkouts/unstable/graphql`, {
      headers,
      body: query,
      method: 'POST',
    });
  } catch (error) {
    return '';
  }
}

/**
 * Define the shopify-payment-terms custom element.
 */
export function defineElement() {
  defineCustomElement('shopify-payment-terms', ShopifyPaymentTerms);
}
